import styled from '@emotion/styled';
import { Box } from './box';

const fontSizeMap: { [key: string]: string } = {
  small: '12px',
  medium: '14px',
  large: '16px',
  xl: '18px',
};

const minHeightMap: { [key: string]: string } = {
  small: '32px',
  medium: '40px',
  large: '48px',
  xl: '72px',
};

export const Button = styled(Box)<{
  type?: string;
  disabled?: boolean;
  disabledToast?: string;
  touching?: boolean;
  size?: 'small' | 'medium' | 'large' | 'xl';
}>`
  outline: none;
  appearance: none;
  border-style: none;
  font-weight: 600;
  min-width: 62px;
  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  text-transform: uppercase;
  color: var(--colorBlack);
  background-color: var(--colorBlueGray100);
  letter-spacing: 0.24px;
  line-height: 16px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease-out;
  &:active {
    background-color: #e6e6e6;
  }
  ${({ disabled }) => disabled && 'opacity: .2;'}
  ${({ disabled, disabledToast }) => disabled && !disabledToast && 'pointer-events: none;'}
  transform: ${({ touching }) => (touching ? 'scale(0.96)' : 'none')};

  /* Size-related styles */
  font-size: ${({ size }) => fontSizeMap[size || 'medium']};
  min-height: ${({ size }) => minHeightMap[size || 'medium']};
`;

Button.defaultProps = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  as: 'button',
  padding: 'small',
  backgroundColor: 'colorBlueGray200',
  size: 'medium',
};

export const OutlinedButton = styled(Button)`
  border: solid 1px #dadada;
  color: black;
`;

export const BlueButton = styled(Button)`
  background: linear-gradient(180deg, #1642be 0.69%, #173792 100%);
  color: white;
`;

export const RedButton = styled(Button)`
  background: var(--colorUIRed);
  color: white;
`;

export const GreenButton = styled(Button)`
  background: linear-gradient(-180deg, rgb(32, 153, 70) 0%, rgb(23, 132, 69) 100%);
  color: white;
`;

export const BlackButton = styled(Button)`
  background: var(--colorBlack);
  color: white;
`;

export const GrayButton = styled(Button)`
  background: var(--colorBlueGray900);
  color: var(--colorBlack);
`;

export const WhiteButton = styled(Button)`
  background: var(--colorWhite);
  color: var(--colorBlueGray1200);
`;

/** Quiet secondary action (cancel / back): hairline border, muted text, lights up under the finger. */
export const GhostButton = styled(Button)`
  background: ${({ touching }) => (touching ? 'var(--colorBlueGray1100)' : 'transparent')};
  border: 1px solid
    ${({ touching }) => (touching ? 'var(--colorBlueGray700)' : 'var(--colorBlueGray1100)')};
  color: ${({ touching }) => (touching ? 'var(--colorBlueGray50)' : 'var(--colorBlueGray300)')};

  &:active {
    background-color: var(--colorBlueGray1100);
  }

  &:hover:not(:disabled) {
    color: var(--colorBlueGray50);
    border-color: var(--colorBlueGray700);
  }
`;

export const TransparentButton = styled(Button)`
  background: transparent;
  color: var(--colorBlueGray200);
  &:active {
    background-color: transparent;
  }
`;
